import { merchantApi } from '@/config/axios'

// ========== 商家端 - 直播带货 API ==========

export interface LiveShopAnchorVO {
  id?: number
  shopId?: number
  anchorUserId?: number
  anchorNickname?: string
  anchorAvatar?: string
  fansCount?: number
  bankAccount?: string
  agreementId?: number
  agreementTitle?: string
  agreementContent?: string
  status?: number
  coopCount?: number
  settleAmount?: number
  bindTime?: string
  createTime?: string
}

export interface LiveCommissionRuleVO {
  id?: number
  shopId?: number
  /** 1 商品，2 主播；同一对象独立保存一项比例。 */
  targetType: 1 | 2
  targetId: number
  ratePercent: number
  createTime?: string
}

export interface LiveAgreementVO {
  id?: number
  shopId?: number
  title?: string
  content?: string
  createTime?: string
}

export interface LiveSettleRecordVO {
  id?: number
  shopId?: number
  anchorUserId?: number
  anchorNickname?: string
  liveId?: number
  liveTitle?: string
  liveTime?: string
  orderCount?: number
  orderAmount?: number
  commissionAmount?: number
  status?: number
  createTime?: string
  paymentRef?: string
  proofFileId?: number
  proofUrl?: string
  paidAt?: string
  settledAt?: string
  settledBy?: number
  settleRemark?: string
}

export interface LiveActivityVO {
  id?: number
  liveId?: number
  liveTitle?: string
  shopId?: number
  /** 1红包 2大转盘 */
  type?: number
  title?: string
  configJson?: string
  status?: number
  startTime?: string
  endTime?: string
  createTime?: string
}

export interface LiveRoomSimpleVO {
  id?: number
  title?: string
  status?: number
  anchorUserId?: number
}

/** 关联主播分页 */
export const getLiveAnchorPage = (params: {
  status?: number
  pageNo?: number
  pageSize?: number
}) => {
  return merchantApi.get({ url: '/merchant-api/live/anchor/page', params })
}

/** 关联主播详情 */
export const getLiveAnchor = (id: number) => {
  return merchantApi.get({ url: '/merchant-api/live/anchor/get', params: { id } })
}

/** 申请/绑定主播 */
export const bindLiveAnchor = (data: LiveShopAnchorVO) => {
  return merchantApi.post({ url: '/merchant-api/live/anchor/bind', data })
}

/** 审核主播 */
export const auditLiveAnchor = (id: number, pass: boolean) => {
  return merchantApi.put({
    url: '/merchant-api/live/anchor/audit',
    params: { id, pass }
  })
}

/** 佣金规则列表 */
export const getLiveCommissionList = () => {
  return merchantApi.get({ url: '/merchant-api/live/commission/list' })
}

/** 保存佣金规则 */
export const saveLiveCommission = (
  data: Pick<LiveCommissionRuleVO, 'targetType' | 'targetId' | 'ratePercent'>
) => {
  return merchantApi.post({ url: '/merchant-api/live/commission/save', data })
}

/** 删除佣金规则 */
export const deleteLiveCommission = (id: number) => {
  return merchantApi.delete({
    url: '/merchant-api/live/commission/delete',
    params: { id }
  })
}

/** 协议列表 */
export const getLiveAgreementList = () => {
  return merchantApi.get({ url: '/merchant-api/live/agreement/list' })
}

/** 保存协议 */
export const saveLiveAgreement = (data: LiveAgreementVO) => {
  return merchantApi.post({ url: '/merchant-api/live/agreement/save', data })
}

/** 结算分页 */
export const getLiveSettlePage = (params: { pageNo?: number; pageSize?: number }) => {
  return merchantApi.get({ url: '/merchant-api/live/settle/page', params })
}

/** 结算详情 */
export const getLiveSettle = (id: number) => {
  return merchantApi.get({ url: '/merchant-api/live/settle/get', params: { id } })
}

/** 上传本店待结算单的转账凭证，使用商家令牌。 */
export const uploadLiveSettleProof = (id: number, file: File) => {
  const data = new FormData()
  data.append('id', String(id))
  data.append('file', file)
  return merchantApi.upload<{ id: number; url: string }>({
    url: '/merchant-api/live/settle/proof',
    data
  })
}

export const confirmLiveOfflineSettle = (data: {
  id: number
  amount: number
  paymentRef: string
  proofFileId: number
  paidAt: string
  remark?: string
}) => merchantApi.post({ url: '/merchant-api/live/settle/confirm-offline', data })

/** 可选直播间简表 */
export const getLiveRoomSimpleList = () => {
  return merchantApi.get({ url: '/merchant-api/live/room/simple-list' })
}

/** 活动分页 */
export const getLiveActivityPage = (params: {
  liveId?: number
  type?: number
  status?: number
  pageNo?: number
  pageSize?: number
}) => {
  return merchantApi.get({ url: '/merchant-api/live/activity/page', params })
}

/** 活动详情 */
export const getLiveActivity = (id: number) => {
  return merchantApi.get({ url: '/merchant-api/live/activity/get', params: { id } })
}

/** 创建红包/大转盘活动 */
export const createLiveActivity = (data: LiveActivityVO) => {
  return merchantApi.post({ url: '/merchant-api/live/activity/create', data })
}

/** 发起活动 */
export const startLiveActivity = (id: number) => {
  return merchantApi.put({ url: '/merchant-api/live/activity/start', params: { id } })
}

export const stopLiveActivity = (id: number) =>
  merchantApi.put({ url: '/merchant-api/live/activity/stop', params: { id } })

export interface LiveRewardVO {
  id: number
  userId: number
  rewardType: string
  amount?: number
  prizeName?: string
  deliveryStatus?: string
  logisticsName?: string
  logisticsNo?: string
  receiver?: {
    name: string
    mobile: string
    areaId: number
    areaName?: string
    detailAddress: string
  }
  createTime?: string
}

export const getLiveRewardPage = (params: {
  activityId: number
  pageNo: number
  pageSize: number
}) => merchantApi.get({ url: '/merchant-api/live/activity/claim-page', params })

export const deliverLiveReward = (data: {
  claimId: number
  logisticsName: string
  logisticsNo: string
}) => merchantApi.put({ url: '/merchant-api/live/activity/reward-delivery', data })

/** 本店直播商品库：只保存商品关联，价格与库存读取商城。 */
export const getLiveProductPage = (params: {
  keyword?: string
  pageNo: number
  pageSize: number
}) => merchantApi.get({ url: '/merchant-api/live/product/page', params })
export const importLiveProducts = (spuIds: number[]) =>
  merchantApi.post({ url: '/merchant-api/live/product/import', data: spuIds })
export const removeLiveProduct = (spuId: number) =>
  merchantApi.delete({ url: '/merchant-api/live/product/remove', params: { spuId } })
